import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, BarChart3, X, Loader2, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getScorecards, ScorecardMetadata } from '../services/scorecardService';
import { deleteScript, listProjectScripts } from '@/services/projectService';

interface ScorecardSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, row click resumes workflow instead of opening the scorecard page. */
  onSelectForWorkflow?: (scorecardId: string) => void;
  projectId?: string;
  title?: string;
  description?: string;
}

function ScorecardSelectionModal({
  open,
  onOpenChange,
  onSelectForWorkflow,
  projectId,
  title = 'Retrieve Previous Analysis',
  description = 'Select a previous analysis to view its scorecard and blueprint.',
}: ScorecardSelectionModalProps) {
  const navigate = useNavigate();
  const [scorecards, setScorecards] = useState<ScorecardMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ScorecardMetadata | null>(null);

  useEffect(() => {
    if (open) {
      void loadScorecards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when picker opens or project changes
  }, [open, projectId, onSelectForWorkflow]);

  const loadScorecards = async () => {
    try {
      setLoading(true);
      setError(null);
      if (onSelectForWorkflow && projectId) {
        const scripts = await listProjectScripts(projectId);
        setScorecards(
          scripts.map((s) => ({
            id: s.analysis_session_id || s.session_id,
            script_title: s.script_title,
            created_at: s.created_at,
            analysis_metadata: {
              overall_score: (s as { overall_score?: number }).overall_score,
              engagement_percentage: (s as { engagement_percentage?: number }).engagement_percentage,
            },
            audience_type: 'decision_maker',
          })) as ScorecardMetadata[],
        );
        return;
      }
      const results = await getScorecards(20, 0);
      setScorecards(results);
    } catch (err: any) {
      console.error('Error loading scorecards:', err);
      setError(err.message || 'Failed to load previous analyses');
    } finally {
      setLoading(false);
    }
  };

  /** Label for scorecard type (Creator vs Decision Maker) for display consistency. */
  const getScorecardTypeLabel = (audienceType: string | null | undefined): string =>
    audienceType === 'creator' ? 'Audience Impact Scorecard' : 'Engagement Scorecard';

  const formatDate = (dateValue: any): string => {
    if (!dateValue) return 'Unknown date';
    try {
      if (dateValue.toDate) {
        return dateValue.toDate().toLocaleDateString();
      }
      if (typeof dateValue === 'string') {
        return new Date(dateValue).toLocaleDateString();
      }
      return 'Unknown date';
    } catch {
      return 'Unknown date';
    }
  };

  const handleSelectScorecard = (scorecardId: string) => {
    onOpenChange(false);
    if (onSelectForWorkflow) {
      onSelectForWorkflow(scorecardId);
      return;
    }
    navigate(`/scorecards/${scorecardId}`);
  };

  const requestDelete = (e: React.MouseEvent, scorecard: ScorecardMetadata) => {
    e.stopPropagation();
    setPendingDelete(scorecard);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const scorecardId = pendingDelete.id;
    try {
      setDeletingId(scorecardId);
      await deleteScript(scorecardId);
      setScorecards((prev) => prev.filter((s) => s.id !== scorecardId));
      setPendingDelete(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete analysis');
      setPendingDelete(null);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-beige border-border-light text-text-dark">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif font-bold text-text-dark">
            {title}
          </DialogTitle>
          <DialogDescription className="text-text-dark opacity-70 font-sans">
            {description}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gold-button" />
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-sm text-red-800 text-sm font-sans">
            {error}
          </div>
        )}

        {!loading && !error && scorecards.length === 0 && (
          <div className="py-12 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-text-dark opacity-30" />
            <h3 className="text-lg font-serif font-semibold text-text-dark mb-2">
              No previous analyses found
            </h3>
            <p className="text-sm text-text-dark opacity-70 font-sans">
              Upload a script to get started with your first analysis.
            </p>
          </div>
        )}

        {!loading && !error && scorecards.length > 0 && (
          <div className="space-y-3 mt-4">
            {scorecards.map((scorecard) => {
              const overallScore = scorecard.analysis_metadata?.overall_score || 0;
              const engagementPercent = scorecard.analysis_metadata?.engagement_percentage || 0;

              return (
                <div
                  key={scorecard.id}
                  className="w-full text-left p-4 bg-beige-dark hover:bg-beige border border-border-light rounded-sm transition-colors cursor-pointer flex items-start justify-between gap-3"
                  onClick={() => handleSelectScorecard(scorecard.id)}
                >
                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-gold-button flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-serif font-semibold text-text-dark">
                          {scorecard.script_title || 'Untitled Script'}
                        </h3>
                        <span className="text-xs text-text-dark opacity-70 font-sans">
                          {getScorecardTypeLabel(scorecard.audience_type)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-text-dark opacity-70 font-sans pl-8">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(scorecard.created_at)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <BarChart3 className="h-4 w-4" />
                        <span>
                          {Math.round(overallScore)}/100 ({Math.round(engagementPercent)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="flex-shrink-0 text-text-dark hover:text-red-600 hover:bg-red-50"
                    aria-label="Delete this analysis"
                    disabled={deletingId === scorecard.id}
                    onClick={(e) => requestDelete(e, scorecard)}
                  >
                    {deletingId === scorecard.id ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Trash2 className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>

      {pendingDelete && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-sm border border-red-200 bg-beige p-6 shadow-lg">
            <h3 className="text-lg font-serif font-semibold text-text-dark">
              Delete &quot;{pendingDelete.script_title || 'Untitled Script'}&quot;?
            </h3>
            <p className="mt-3 text-sm font-sans text-text-dark/70">
              This permanently deletes the script and all of its analysis, scorecard, rebuild data
              and uploaded files. This action is irreversible and cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={deletingId === pendingDelete.id}
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={deletingId === pendingDelete.id}
                onClick={() => void confirmDelete()}
              >
                {deletingId === pendingDelete.id ? 'Deleting…' : 'Delete script'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default ScorecardSelectionModal;
